import { useEffect, useState } from "react";
import { Accordion, Badge, Box, Text, Tooltip } from "@mantine/core";
import {
  IconChartLine,
  IconSettings,
  IconBell,
  IconRobot,
  IconList,
  IconLayout2,
} from "@tabler/icons-react";
import { getWidgetsByCategory } from "./registry";
import { WidgetDefinition } from "@pulse-ui/widget-sdk";
import { useRbac, widgetGate } from "@pulse-ui/rbac";
import { IconGripVertical } from "@tabler/icons-react";

const WIDGET_DRAG_MIME_TYPE = "application/x-halo-widget";

const CATEGORY_ICONS: Record<string, React.ElementType> = {
  Trading: IconChartLine,
  Management: IconSettings,
  Alerts: IconBell,
  Algo: IconRobot,
  Blotter: IconList,
};

function getCategoryIcon(category: string): React.ElementType {
  return CATEGORY_ICONS[category] ?? IconLayout2;
}

interface WidgetLibraryProps {
  widgetRegistry?: Record<string, WidgetDefinition>;
  onWidgetSelect?: (widgetId: string) => void;
  /** Called when a widget is double-clicked; use to add it to the layout */
  onWidgetActivate?: (widgetId: string) => void;
  /** When set, this category is opened and all others are closed */
  focusCategory?: string | null;
  /** Called when user clicks an icon in collapsed sidebar mode */
  onCategoryIconClick?: (category: string) => void;
  /** Whether the sidebar is collapsed (icon-only mode) */
  collapsed?: boolean;
}

export function WidgetLibrary({
  widgetRegistry,
  onWidgetSelect,
  onWidgetActivate,
  focusCategory,
  onCategoryIconClick,
  collapsed = false,
}: WidgetLibraryProps) {
  const { roles } = useRbac();

  const widgetsByCategory = widgetRegistry
    ? groupWidgetsByCategory(widgetRegistry, roles)
    : getWidgetsByCategory();

  const categories = Object.keys(widgetsByCategory);

  const [openCategories, setOpenCategories] = useState<string[]>(categories);

  // Sync external focusCategory: ensure it's open without closing the others.
  useEffect(() => {
    if (focusCategory != null) {
      setOpenCategories((prev) =>
        prev.includes(focusCategory) ? prev : [...prev, focusCategory],
      );
    }
  }, [focusCategory]);

  const handleDragStart = (e: React.DragEvent, widget: WidgetDefinition) => {
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData(WIDGET_DRAG_MIME_TYPE, widget.id);
    e.dataTransfer.setData("text/plain", widget.name);
  };

  if (categories.length === 0) {
    return (
      <Box p="md">
        <Text size="sm" c="dimmed">
          No widgets available.
        </Text>
      </Box>
    );
  }

  // Collapsed: show only category icons stacked vertically
  if (collapsed) {
    return (
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4,
          paddingTop: 8,
        }}
      >
        {categories.map((category) => {
          const Icon = getCategoryIcon(category);
          return (
            <Tooltip key={category} label={category} position="right" withArrow>
              <Box
                onClick={() => onCategoryIconClick?.(category)}
                style={{
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "var(--mantine-radius-md)",
                  cursor: "pointer",
                  color: "var(--mantine-color-dimmed)",
                  transition: "background-color 120ms ease, color 120ms ease",
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.backgroundColor =
                    "var(--mantine-color-default-hover)";
                  el.style.color = "var(--mantine-color-text)";
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.backgroundColor = "transparent";
                  el.style.color = "var(--mantine-color-dimmed)";
                }}
              >
                <Icon size={18} />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    );
  }

  // Expanded: accordion with single-open category
  return (
    <Accordion
      multiple
      value={openCategories}
      onChange={(val) => setOpenCategories(val)}
      chevronPosition="right"
      styles={{
        root: { padding: "4px 0" },
        item: { border: "none" },
        control: {
          padding: "8px 12px",
          borderRadius: "var(--mantine-radius-sm)",
          transition: "background-color 120ms ease",
          "&:hover": { backgroundColor: "var(--mantine-color-default-hover)" },
        },
        label: { padding: 0 },
        content: { padding: "2px 6px 6px" },
        chevron: { color: "var(--mantine-color-dimmed)" },
      }}
    >
      {categories.map((category) => {
        const Icon = getCategoryIcon(category);
        const count = widgetsByCategory[category].length;
        return (
          <Accordion.Item key={category} value={category}>
            <Accordion.Control
              icon={
                <Icon
                  size={14}
                  style={{ color: "var(--mantine-color-dimmed)" }}
                />
              }
            >
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                }}
              >
                <Text
                  size="xs"
                  fw={600}
                  c="dimmed"
                  tt="uppercase"
                  style={{ letterSpacing: "0.05em" }}
                >
                  {category}
                </Text>
                <Badge size="xs" variant="light" color="gray" radius="sm">
                  {count}
                </Badge>
              </Box>
            </Accordion.Control>
            <Accordion.Panel>
              <Box style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {widgetsByCategory[category].map((widget) => (
                  <Tooltip
                    key={widget.id}
                    label={
                      widget.disabled
                        ? "Coming soon"
                        : "Double-click to add, or drag"
                    }
                    disabled={widget.disabled ? false : !onWidgetActivate}
                    position="right"
                    withArrow
                  >
                    <Box
                      draggable={!widget.disabled}
                      onDragStart={
                        widget.disabled
                          ? undefined
                          : (e) => handleDragStart(e, widget)
                      }
                      onClick={
                        widget.disabled
                          ? undefined
                          : () => onWidgetSelect?.(widget.id)
                      }
                      onDoubleClick={
                        widget.disabled
                          ? undefined
                          : () => onWidgetActivate?.(widget.id)
                      }
                      px="sm"
                      py={6}
                      style={{
                        cursor: widget.disabled ? "default" : "grab",
                        borderRadius: "var(--mantine-radius-sm)",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 8,
                        transition: "background-color 120ms ease",
                        opacity: widget.disabled ? 0.45 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (widget.disabled) return;
                        (e.currentTarget as HTMLElement).style.backgroundColor =
                          "var(--mantine-color-default-hover)";
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.backgroundColor =
                          "transparent";
                      }}
                    >
                      <IconGripVertical
                        size={14}
                        style={{
                          marginTop: 3,
                          flexShrink: 0,
                          color: "var(--mantine-color-dimmed)",
                        }}
                      />
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text
                          size="sm"
                          fw={500}
                          c="var(--mantine-color-text)"
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {widget.name}
                          {widget.disabled && (
                            <Badge
                              size="xs"
                              variant="outline"
                              color="gray"
                              ml={6}
                              style={{ verticalAlign: "middle" }}
                            >
                              Soon
                            </Badge>
                          )}
                        </Text>
                        <Text
                          size="xs"
                          c="dimmed"
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {widget.description}
                        </Text>
                      </Box>
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </Accordion.Panel>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
}

function groupWidgetsByCategory(
  registry: Record<string, WidgetDefinition>,
  userRoles: readonly string[] = [],
): Record<string, WidgetDefinition[]> {
  const byCategory: Record<string, WidgetDefinition[]> = {};
  Object.values(registry).forEach((widget) => {
    if (widget.hideFromLibrary) return;
    if (!widgetGate(widget, userRoles)) return;
    if (!byCategory[widget.category]) byCategory[widget.category] = [];
    byCategory[widget.category].push(widget);
  });
  return byCategory;
}
